import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

export type InlineMessageType = 'success' | 'error' | 'info' | 'warning';

/** InlineMessage - animated inline alert with icon, auto-dismiss, and optional close button. */
interface InlineMessageProps {
  readonly type: InlineMessageType;
  readonly message: string;
  readonly visible: boolean;
  readonly onDismiss?: () => void;
  readonly autoDismissMs?: number;
}

const iconMap: Record<InlineMessageType, keyof typeof Ionicons.glyphMap> = {
  success: 'checkmark-circle',
  error: 'alert-circle',
  info: 'information-circle',
  warning: 'warning',
};

export default function InlineMessage({ type, message, visible, onDismiss, autoDismissMs = 4000 }: InlineMessageProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }).start();
      if (autoDismissMs > 0 && onDismiss) {
        const timer = setTimeout(onDismiss, autoDismissMs);
        return () => clearTimeout(timer);
      }
    } else {
      Animated.timing(opacity, { toValue: 0, duration: 150, useNativeDriver: true }).start();
    }
  }, [visible]);

  if (!visible) return null;

  const colorMap: Record<InlineMessageType, string> = {
    success: colors.success,
    error: colors.danger,
    info: colors.primary,
    warning: colors.warning,
  };
  const color = colorMap[type];

  return (
    <Animated.View style={[styles.container, { opacity, borderColor: color + '40', backgroundColor: color + '10' }]}>
      <Ionicons name={iconMap[type]} size={18} color={color} />
      <Text style={[styles.message, { color }]}>{message}</Text>
      {onDismiss && (
        <TouchableOpacity onPress={onDismiss} hitSlop={8}>
          <Ionicons name="close" size={16} color={colors.textLight} />
        </TouchableOpacity>
      )}
    </Animated.View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    marginTop: spacing.sm,
  },
  message: {
    flex: 1,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
